import React, { useState } from 'react';
import { useTranslation } from '../../i18n/provider';
import styles from './RestTimer.module.css';

export type RestTimerProps = {
  label: string; // e.g. "01:30"
  onStart: () => void;
  onPause: () => void;
  onReset: () => void;
  onPreset: (seconds: number) => void;
  canPause?: boolean;
  hasStarted?: boolean;
  onMinimize?: () => void;
};

const RestTimer: React.FC<RestTimerProps> = ({ label, onStart, onPause, onReset, onPreset, canPause = false, hasStarted = false, onMinimize }) => {
  const { t } = useTranslation();
  const isRunning = canPause;
  const [showTimePicker, setShowTimePicker] = useState(false);
  
  // Generate time options from 10s to 30min (1800s) in 5s increments
  const timeOptions: number[] = [];
  for (let i = 10; i <= 1800; i += 5) {
    timeOptions.push(i);
  }

  const formatTimeOption = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) {
      return `${secs}s`;
    } else if (secs === 0) {
      return `${mins}m`;
    } else {
      return `${mins}m ${secs}s`;
    }
  };

  const handlePresetClick = (seconds: number) => {
    onPreset(seconds);
    setShowTimePicker(false);
  };

  return (
    <div className={styles.timerContainer}>
      <div className={styles.timerContent}>
        <div className={styles.timerDisplay}>
          <div className={styles.timerLabel}>{t('shared.restTimer.label')}</div>
          <div className={styles.timerValue}>{label}</div>
        </div>
        <div className={styles.timerControls}>
          <div className={styles.controlButtonStack}>
            {!isRunning ? (
              <button className={styles.controlButton} onClick={onStart} title={t('shared.restTimer.start')}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </button>
            ) : (
              <button className={styles.controlButton} onClick={onPause} title={t('shared.restTimer.pause')}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                </svg>
              </button>
            )}
            <button className={styles.controlButton} onClick={onReset} title={t('shared.restTimer.reset')}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                <path d="M21 3v5h-5"/>
                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                <path d="M3 21v-5h5"/>
              </svg>
            </button>
          </div>
        </div>
        <div className={styles.timerPresets}>
          <button className={styles.presetButton} onClick={() => onPreset(60)}>60s</button>
          <button className={styles.presetButton} onClick={() => onPreset(90)}>90s</button>
          <button className={styles.presetButton} onClick={() => onPreset(120)}>2m</button>
          <button 
            className={styles.moreButton} 
            onClick={() => setShowTimePicker(!showTimePicker)}
            title={t('shared.restTimer.moreOptions')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </button>
        </div>
        {onMinimize && (
          <div>
            <button 
              className={styles.controlButton} 
              onClick={onMinimize} 
              title={t('shared.restTimer.minimize')}
              style={{ backgroundColor: '#333', color: '#4CAF50', border: '1px solid #444' }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="4 14 10 14 10 20"></polyline>
                <polyline points="20 10 14 10 14 4"></polyline>
                <line x1="14" y1="10" x2="21" y2="3"></line>
                <line x1="3" y1="21" x2="10" y2="14"></line>
              </svg>
            </button>
          </div>
        )}
      </div>
      
      {showTimePicker && (
        <div className={styles.timePickerOverlay} onClick={() => setShowTimePicker(false)}>
          <div className={styles.timePickerModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.timePickerHeader}>
              <h3>{t('shared.restTimer.selectTime')}</h3>
              <button className={styles.closeButton} onClick={() => setShowTimePicker(false)} aria-label={t('shared.common.close')}>✕</button>
            </div>
            <div className={styles.timePickerList}>
              {timeOptions.map(seconds => (
                <button
                  key={seconds}
                  className={styles.timeOption}
                  onClick={() => handlePresetClick(seconds)}
                >
                  {formatTimeOption(seconds)}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RestTimer;
